if (!customElements.get('quantity-break-picker')) {
  customElements.define(
    'quantity-break-picker',
    class QuantityBreakPicker extends HTMLElement {
      constructor() {
        super();
        this.boundOnChange = this.onChange.bind(this);
        this.currentDiscount = 0;
      }

      connectedCallback() {
        this.inputs = this.querySelectorAll('input[type="radio"]');
        this.priceElements = this.querySelectorAll('.qty-break-card__price');
        this.boundOnVariantChange = this.onVariantChange.bind(this);

        this.inputs.forEach((input) => {
          input.addEventListener('change', this.boundOnChange);
        });

        document.addEventListener('variant:changed', this.boundOnVariantChange);

        this.querySelectorAll('.qty-break-card').forEach((card) => {
          card.addEventListener('click', (e) => {
            if (e.target.matches('input, label, label *')) return;

            const input = card.querySelector('input[type="radio"]');
            if (input && !input.disabled && !input.checked) {
              input.checked = true;
              this.updateSelectedState(input);
              this.setQuantity(input);
              this.updateSelectedLabel(input.value);
            }
          });
        });

        const checkedInput = this.querySelector('input[type="radio"]:checked');

        if (checkedInput) {
          this.setQuantity(checkedInput);
        }

        this.observeSellingPlan();
      }

      disconnectedCallback() {
        this.inputs.forEach((input) => {
          input.removeEventListener('change', this.boundOnChange);
        });

        document.removeEventListener('variant:changed', this.boundOnVariantChange);

        if (this.sellingPlanObserver) {
          this.sellingPlanObserver.disconnect();
        }
      }

      onVariantChange({ detail: { variant } }) {
        if (!variant) return;

        const tracksInventory =
          variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny';

        this.inputs.forEach((input) => {
          const qty = parseInt(input.dataset.quantity, 10);
          const card = input.closest('.qty-break-card');
          const unavailable = tracksInventory && variant.inventory_quantity < qty;

          input.disabled = unavailable;
          card.classList.toggle('qty-break-card--disabled', unavailable);

          const priceEl = card.querySelector('.qty-break-card__price');
          if (priceEl) priceEl.hidden = unavailable;

          if (unavailable && input.checked) {
            input.checked = false;
            card.classList.remove('qty-break-card--selected');
          }
        });

        // Re-apply selected quantity — variant change resets the quantity input
        const checkedInput = this.querySelector('input[type="radio"]:checked');
        if (checkedInput) {
          this.setQuantity(checkedInput);
        }
      }

      observeSellingPlan() {
        const formId = this.dataset.productFormId;

        // The selling_plan input lives outside the form (inside <skio-plan-picker>),
        // linked via the HTML form attribute. Search globally by that attribute.
        const sellingPlanInput = formId
          ? document.querySelector(`input[name="selling_plan"][form="${formId}"]`)
          : null;

        if (sellingPlanInput) {
          this.sellingPlanObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                this.onSellingPlanChange(sellingPlanInput.value);
              }
            });
          });

          this.sellingPlanObserver.observe(sellingPlanInput, {
            attributes: true,
            attributeFilter: ['value'],
          });

          sellingPlanInput.addEventListener('change', (e) => {
            this.onSellingPlanChange(e.target.value);
          });
        }

        // Also listen for Skio's custom event as a reliable signal
        document.addEventListener('skio::update-selling-plan', (e) => {
          const sellingPlan = e.detail?.sellingPlan;
          if (sellingPlan) {
            const discount = this.getSkioDiscount(sellingPlan);
            this.currentDiscount = discount;
            this.updatePrices(discount);
          } else {
            this.currentDiscount = 0;
            this.updatePrices(0);
          }
        });
      }

      onSellingPlanChange(sellingPlanValue) {
        if (sellingPlanValue && sellingPlanValue !== '') {
          // Try Skio component first
          const skioPicker = document.querySelector('skio-plan-picker');
          if (skioPicker && skioPicker.selectedSellingPlan) {
            const discount = this.getSkioDiscount(skioPicker.selectedSellingPlan);
            this.currentDiscount = discount;
            this.updatePrices(discount);
          } else {
            // Fallback: read discount from the active frequency button (custom widget)
            const activeFreqBtn = document.querySelector(
              `.rc-widget-custom__freq-btn[data-selling-plan-id="${sellingPlanValue}"]`
            );
            const discount = activeFreqBtn
              ? parseInt(activeFreqBtn.dataset.discount || '0', 10)
              : this.currentDiscount;
            this.currentDiscount = discount;
            this.updatePrices(discount);
          }
        } else {
          this.currentDiscount = 0;
          this.updatePrices(0);
        }
      }

      getSkioDiscount(sellingPlan) {
        if (!sellingPlan || !sellingPlan.price_adjustments || !sellingPlan.price_adjustments.length) {
          return 0;
        }
        const adj = sellingPlan.price_adjustments[0];
        if (adj.value_type === 'percentage') {
          return adj.value;
        }
        // For fixed_amount or price types, we can't express as a simple percentage
        // without knowing the variant price, so return 0 and let Skio handle display
        return 0;
      }

      updatePrices(discountPercent) {
        const isSubscription = discountPercent > 0;

        this.priceElements.forEach((priceEl) => {
          const originalPrice = parseInt(priceEl.dataset.originalPrice, 10);
          const subscriptionPrice = parseInt(priceEl.dataset.subscriptionPrice, 10);
          if (!originalPrice) return;

          // Subscription: use the selling plan allocation price from Shopify
          // (no bulk discount). One-off: show the tier-discounted price.
          const displayPrice = isSubscription && subscriptionPrice
            ? subscriptionPrice
            : originalPrice;

          const formattedPrice = this.formatMoney(displayPrice);
          priceEl.textContent = `${formattedPrice} each`;
        });
      }

      formatMoney(cents) {
        const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        const amount = (cents / 100).toFixed(2);
        const amountNoDecimals = Math.floor(cents / 100);
        const amountWithComma = amount.replace('.', ',');

        const amountWithThousands = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const amountNoDecimalsWithThousands = amountNoDecimals
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

        return moneyFormat
          .replace('{{amount_with_comma_separator}}', amountWithComma)
          .replace('{{amount_no_decimals}}', amountNoDecimalsWithThousands)
          .replace('{{amount}}', amountWithThousands);
      }

      onChange(event) {
        const target = event.target;
        this.dataset.selectedQuantity = target.dataset.quantity;
        this.updateSelectedState(target);
        this.setQuantity(target);
        this.updateSelectedLabel(target.value);
        this.updateWidgetPrices();
      }

      updateWidgetPrices() {
        const checkedInput = this.querySelector('input[type="radio"]:checked');
        if (!checkedInput) return;

        const qty = parseInt(checkedInput.dataset.quantity, 10) || 1;
        const tierPriceEl = checkedInput.closest('.qty-break-card')?.querySelector('.qty-break-card__price');
        const tierPrice = tierPriceEl ? parseInt(tierPriceEl.dataset.originalPrice, 10) : null;
        const subscriptionPrice = tierPriceEl ? parseInt(tierPriceEl.dataset.subscriptionPrice, 10) : null;

        // Update one-time purchase price (tier-discounted * qty)
        if (tierPrice) {
          const onetimeOption = document.querySelector('.rc-widget-custom__option[data-type="onetime"]');
          if (onetimeOption) {
            onetimeOption.querySelectorAll('.rc-widget-custom__price[data-base-price]').forEach((span) => {
              span.textContent = this.formatMoney(tierPrice * qty);
            });
          }
        }

        // Update subscription price (flat subscription price * qty)
        if (subscriptionPrice) {
          const subOption = document.querySelector('.rc-widget-custom__option[data-type="subscription"]');
          if (subOption) {
            subOption.querySelectorAll('.rc-widget-custom__price[data-base-price]').forEach((span) => {
              span.textContent = this.formatMoney(subscriptionPrice * qty);
            });
          }
        }
      }

      setQuantity(input) {
        const qty = parseInt(input.dataset.quantity, 10);
        if (!qty) return;

        const formId = this.dataset.productFormId;

        let qtyInput = formId
          ? document.querySelector(`[name="quantity"][form="${formId}"]`)
          : null;

        if (!qtyInput) {
          const form = this.getProductForm();
          if (form) qtyInput = form.querySelector('[name="quantity"]');
        }

        if (qtyInput) qtyInput.value = qty;
      }

      getProductForm() {
        const formId = this.dataset.productFormId;
        if (formId) return document.getElementById(formId);
        return document.querySelector('form[data-type="add-to-cart-form"]');
      }

      updateSelectedState(selectedInput) {
        this.querySelectorAll('.qty-break-card').forEach((card) => {
          card.classList.remove('qty-break-card--selected');
        });

        const selectedCard = selectedInput.closest('.qty-break-card');
        if (selectedCard) {
          selectedCard.classList.add('qty-break-card--selected');
        }
      }

      updateSelectedLabel(value) {
        const label = this.querySelector('[data-selected-swatch-value]');
        if (label) {
          label.textContent = value;
        }
      }
    }
  );
}
